import Vue from 'vue'

// Track direction via Navigation API (fires before beforeEach for back/forward traversals)
let _isBack = false
if (typeof window !== 'undefined' && window.navigation) {
  window.navigation.addEventListener('navigate', (event) => {
    _isBack = event.navigationType === 'traverse' &&
      event.destination.index < window.navigation.currentEntry.index
  })
}

// Active transition context
let _ctx = null  // { transitionItemId, projectId, sourceEl, sourceProjEl, resolve }

function esc(id) { return CSS.escape(String(id)) }

/**
 * Called by router beforeEach BEFORE startViewTransition.
 * Imperatively sets view-transition-name on source elements so they're
 * captured in the old state snapshot.
 */
export function startTransition({ transitionItemId, projectId, resolve }) {
  cleanup()

  const sourceEl = transitionItemId
    ? (document.querySelector(`[data-item-id="${esc(transitionItemId)}"] img`)
      || document.querySelector('.ds-page .frame-renderer__thumbnail'))
    : null
  if (sourceEl) sourceEl.style.viewTransitionName = 'ds-item-active'

  const sourceProjEl = projectId
    ? (document.querySelector(`[data-project-id="${esc(projectId)}"] .project-lane__name`)
      || document.querySelector(`[data-vt-project-header="${esc(projectId)}"] .project-title`))
    : null
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = `project-header`

  _ctx = { transitionItemId, projectId, sourceEl, sourceProjEl, resolve }
}

/**
 * Called by the destination component when its transition element is rendered.
 * Pass destEl explicitly to avoid keep-alive DOM collisions (scoped to component $el).
 */
export function signalReady(destEl = null) {
  if (!_ctx?.resolve) return
  const { transitionItemId, projectId, sourceEl, sourceProjEl, resolve } = _ctx

  // Clear source names — old state was already captured by startViewTransition
  if (sourceEl) sourceEl.style.viewTransitionName = ''
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = ''

  // Set destination item name (use passed element, or fall back to document query)
  const itemTarget = destEl || (transitionItemId
    ? (() => {
        const all = [...document.querySelectorAll(`[data-item-id="${esc(transitionItemId)}"] img`)]
        return all.find(el => el !== sourceEl) || null
      })()
    : null)
  if (itemTarget) itemTarget.style.viewTransitionName = 'ds-item-active'

  // Set destination project header name
  if (projectId) {
    const all = [...document.querySelectorAll(
      `[data-project-id="${esc(projectId)}"] .project-lane__name, [data-vt-project-header="${esc(projectId)}"] .project-title`
    )]
    const projTarget = all.find(el => el !== sourceProjEl) || null
    if (projTarget) projTarget.style.viewTransitionName = `project-header`
  }

  const fn = resolve
  _ctx.resolve = null  // prevent double-call
  Vue.nextTick(fn)
}

/**
 * Called by router transition.finished to clear all imperative VT names.
 */
export function cleanup() {
  if (!_ctx) return
  const { sourceEl, sourceProjEl } = _ctx
  if (sourceEl) sourceEl.style.viewTransitionName = ''
  if (sourceProjEl) sourceProjEl.style.viewTransitionName = ''
  // Sweep any ds-item-active or project-header-* names left on elements
  document.querySelectorAll('[style*="view-transition-name"]').forEach(el => {
    const vtn = el.style.viewTransitionName
    if (vtn && vtn !== 'none') el.style.viewTransitionName = ''
  })
  _ctx = null
}

export function getContext() { return _ctx }
export function isBack() { return _isBack }
